import { useMemo } from 'react';
import { useWarehouseModels, type ItemModel } from '@/api/capacities';

/**
 * Shared warehouse-model access for the Server.VBM config rows. Models are
 * queried LIVE from the warehouse service (`useWarehouseModels`) rather than a
 * bundled snapshot, so the catalog stays in sync with inventory. Rows filter by
 * the option `name` (server/disk/memory) → model `category`, and read the
 * capacity spec (CPU cores / disk-memory CAPACITY) off `specifications`.
 */

/** Config option `name` → the warehouse model `category` values it accepts. */
export const TYPE_TO_WAREHOUSE_TYPES: Record<string, string[]> = {
  server: ['SERVER'],
  disk: ['SSD', 'HDD'],
  memory: ['RAM'],
};

/** Which spec carries capacity for each option group. */
export const TYPE_TO_CAPACITY_SPEC: Record<string, string> = {
  server: 'CPU',
  disk: 'CAPACITY',
  memory: 'CAPACITY',
};

export interface VbmModels {
  models: ItemModel[];
  isLoading: boolean;
  /** Distinct brands across the catalog — options for the read-only Brand field. */
  manufactureOptions: { label: string; value: string }[];
}

export function useVbmModels(): VbmModels {
  const { data: models = [], isLoading } = useWarehouseModels();

  const manufactureOptions = useMemo(() => {
    const brands = Array.from(new Set(models.map((m) => m.brand).filter(Boolean) as string[]));
    return brands.map((b) => ({ label: b, value: b }));
  }, [models]);

  return { models, isLoading, manufactureOptions };
}

/** Models whose category matches any of `categories` (case-insensitive). */
export function modelsForCategories(models: ItemModel[], categories: string[]) {
  return models.filter((m) => categories.includes(m.category.toUpperCase()));
}

/** Capacity spec (value + unit) for a model, matched by spec name (CPU/CAPACITY). */
export function capacitySpec(model: ItemModel | undefined, specName: string) {
  const s = model?.specifications?.find((sp) => sp.name.toUpperCase() === specName.toUpperCase());
  return { value: Number(s?.value ?? 0), unit: (s?.unit ?? '').toLowerCase() };
}
